import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight, TrendUp, Briefcase, FileText, PenNib, ListChecks, WarningCircle } from "@phosphor-icons/react";

const Stat = ({ label, value, mono = true, accent = false }) => (
  <div className="card-flat fade-up">
    <p className="label-eyebrow">{label}</p>
    <p
      className={`mt-4 leading-none ${mono ? "font-mono" : "font-serif"} ${
        accent ? "text-[#A8977B]" : "text-[#112018]"
      } text-4xl md:text-5xl`}
    >
      {value}
    </p>
  </div>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [myTasks, setMyTasks] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/my-tasks").then((r) => setMyTasks(r.data.items));
  }, []);

  const stats = summary?.stats;

  return (
    <div data-testid="dashboard-page" className="space-y-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-[#1A2F24]/12">
        <div>
          <p className="label-eyebrow">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-[#112018] leading-[1.05] mt-2">
            Good {new Date().getHours() < 12 ? "morning" : "afternoon"},{" "}
            <em className="text-[#A8977B] not-italic">{user?.name?.split(" ")[0]}</em>.
          </h1>
          <p className="mt-3 text-[#606C64] max-w-xl">
            A measured view of the week — engagements in motion, documents in flight, and the
            people behind every signature.
          </p>
        </div>
        <Link to="/engagements" className="btn-primary" data-testid="dash-cta-engagements">
          New Engagement
          <ArrowUpRight size={14} className="inline ml-2" />
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <Stat label="Active Engagements" value={stats?.active_engagements ?? 0} accent />
        <Stat label="Signed Letters" value={stats?.signed_letters ?? 0} />
        <Stat label="Open Tasks" value={stats?.open_tasks ?? 0} />
        <Stat
          label="Fees Managed"
          value={`$${(stats?.total_fees ?? 0).toLocaleString()}`}
        />
      </div>

      {(stats?.overdue_tasks ?? 0) > 0 && (
        <div
          className="border border-[#C94A3D] bg-[#C94A3D]/5 px-5 py-3 flex items-center gap-3"
          data-testid="overdue-banner"
        >
          <WarningCircle size={20} className="text-[#C94A3D]" weight="fill" />
          <p className="text-sm text-[#C94A3D]">
            <strong>{stats.overdue_tasks}</strong> overdue{" "}
            {stats.overdue_tasks === 1 ? "task" : "tasks"} across your firm.
          </p>
        </div>
      )}

      {/* Recent + Quick actions */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-flat">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="label-eyebrow">Recent</p>
              <h2 className="font-serif text-2xl text-[#112018]">Latest Engagements</h2>
            </div>
            <Link
              to="/engagements"
              className="text-sm text-[#1A2F24] underline underline-offset-4 hover:text-[#A8977B]"
            >
              View all
            </Link>
          </div>
          {!summary?.recent?.length ? (
            <div className="py-12 text-center text-[#606C64]">
              <p className="font-serif text-2xl text-[#112018]">No engagements yet.</p>
              <p className="mt-2 text-sm">
                Create your first engagement to begin tracking the workflow.
              </p>
              <Link to="/engagements" className="btn-outline inline-block mt-6">
                Create one
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-[#1A2F24]/12">
              {summary.recent.map((e) => (
                <Link
                  key={e.id}
                  to={`/engagements/${e.id}`}
                  className="flex items-center justify-between py-4 hover:bg-[#F7F6F2] px-2 -mx-2 transition-colors"
                >
                  <div>
                    <p className="text-[#112018] font-medium">{e.client_name}</p>
                    <p className="label-eyebrow mt-1">{e.service_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-[#1A2F24]">
                      ${e.fee.toLocaleString()}
                    </p>
                    <p className="label-eyebrow mt-1">
                      Step {(e.current_step ?? 0) + 1} / 7
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card-flat">
          <p className="label-eyebrow">Quick Actions</p>
          <h2 className="font-serif text-2xl text-[#112018] mt-1">Get moving</h2>
          <div className="mt-6 space-y-3">
            <QuickLink to="/engagements" icon={Briefcase} label="Manage engagements" />
            <QuickLink to="/documents" icon={FileText} label="Upload documents" />
            <QuickLink to="/mini-apps" icon={PenNib} label="Build a mini-app" />
            <QuickLink to="/integrations" icon={TrendUp} label="Connect Slack & Gmail" />
          </div>
        </div>
      </div>

      {/* My Tasks widget */}
      <div className="card-flat" data-testid="my-tasks-widget">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="label-eyebrow">My Plate</p>
            <h2 className="font-serif text-2xl text-[#112018] mt-1">
              Open tasks · {myTasks.length}
            </h2>
          </div>
          <ListChecks size={20} className="text-[#A8977B]" />
        </div>
        {myTasks.length === 0 ? (
          <p className="text-sm text-[#606C64] italic py-6 text-center">
            Nothing on your plate. Open an engagement to plan work.
          </p>
        ) : (
          <div className="divide-y divide-[#1A2F24]/12">
            {myTasks.slice(0, 8).map((t) => {
              const today = new Date().toISOString().slice(0, 10);
              const overdue = t.due_date && t.due_date < today;
              return (
                <Link
                  key={t.id}
                  to={`/engagements/${t.engagement_id}`}
                  className="flex items-center justify-between py-3 hover:bg-[#F7F6F2] px-2 -mx-2 transition-colors"
                  data-testid={`my-task-${t.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#112018] truncate">{t.title}</p>
                    {t.engagement && (
                      <p className="label-eyebrow mt-1">
                        {t.engagement.client_name} · {t.engagement.service_type}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-3">
                    {t.due_date && (
                      <span
                        className={`label-eyebrow ${
                          overdue ? "text-[#C94A3D]" : ""
                        }`}
                      >
                        {t.due_date}
                      </span>
                    )}
                    <span
                      className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest"
                      style={{
                        background:
                          t.priority === "high"
                            ? "#C94A3D"
                            : t.priority === "medium"
                              ? "#A8977B"
                              : "#efece3",
                        color:
                          t.priority === "high"
                            ? "#fff"
                            : t.priority === "medium"
                              ? "#1A2F24"
                              : "#606C64",
                      }}
                    >
                      {t.priority}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const QuickLink = ({ to, icon: Icon, label }) => (
  <Link
    to={to}
    className="flex items-center gap-3 px-4 py-3 border border-[#1A2F24]/12 hover:border-[#1A2F24] transition-colors group"
  >
    <Icon size={18} className="text-[#A8977B]" />
    <span className="text-sm text-[#112018] flex-1">{label}</span>
    <ArrowUpRight
      size={14}
      className="text-[#606C64] group-hover:text-[#1A2F24] group-hover:translate-x-0.5 transition-all"
    />
  </Link>
);

export default DashboardPage;
